// Firebase App Check wiring.
//
// App Check proves a request came from our real site (attested via reCAPTCHA
// Enterprise) rather than a script hitting the API directly. The token is sent
// on each API call as the `X-Firebase-AppCheck` header and verified by the
// backend.
//
// This is a no-op unless the Firebase config + reCAPTCHA key are provided via
// env vars, so builds without them (and local dev) are unaffected. The backend
// only *requires* the token once APP_CHECK_ENFORCE is turned on, so the two
// sides can be rolled out independently.

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAppCheck,
  getToken,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';

const env = import.meta.env;

const recaptchaKey = env.VITE_RECAPTCHA_ENTERPRISE_KEY as string | undefined;
const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
  projectId: env.VITE_FIREBASE_PROJECT_ID as string | undefined,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET as string | undefined,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID as string | undefined,
  appId: env.VITE_FIREBASE_APP_ID as string | undefined,
};

let appCheck: AppCheck | null = null;

/** True when enough config is present to initialise App Check. */
const isConfigured = (): boolean => Boolean(recaptchaKey && firebaseConfig.appId && firebaseConfig.apiKey);

/** Initialise Firebase + App Check once, at app startup. Safe to call when unconfigured. */
export function initAppCheck(): void {
  if (appCheck || !isConfigured()) return;

  // In dev, register a debug token (printed to the console on first run) in the
  // Firebase App Check console so localhost requests verify.
  if (env.DEV && env.VITE_APPCHECK_DEBUG_TOKEN) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = env.VITE_APPCHECK_DEBUG_TOKEN;
  }

  try {
    const app: FirebaseApp = initializeApp(firebaseConfig as Record<string, string>);
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaKey as string),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // Never let App Check setup break the app shell.
    console.error('App Check init failed:', err);
    appCheck = null;
  }
}

/**
 * Current App Check token, or null when unconfigured/unavailable. Never throws —
 * a failure here must not block the underlying API request.
 */
export async function getAppCheckToken(): Promise<string | null> {
  if (!appCheck) return null;
  try {
    const result = await getToken(appCheck, /* forceRefresh */ false);
    return result.token;
  } catch (err) {
    console.error('App Check getToken failed:', err);
    return null;
  }
}
